import { useMemo, useState } from 'react';
import { Button } from '@understory-io/leaf/button';
import { css } from '@understory-io/leaf/css';
import { FormattedMessage, useIntl } from 'react-intl';
import {
  useLocation,
  useNavigation,
  useSearchParams,
  useSubmit,
} from 'react-router';
import {
  CategoryFilter,
  ExperienceFilter,
  FILTER_SHEET_MESSAGES,
  FilterBar,
  LanguageFilter,
  LocationFilter,
  pick,
  type FilterSheetField,
} from '~/ui/filters';
import { DateFromFilter } from '~/ui/filters/date-from-filter';
import {
  DeferredFilterSheet as FilterSheet,
  usePreloadFilterSheet,
} from '~/ui/deferred/deferred-menus';
import { SearchIcon } from '~/ui/icons/search';
import { buildFilterSearch, type FilterUpdates } from './filter-search';
import type { UpcomingEventsPageData } from './page.server';
import {
  trackDatePickerOpened,
  trackDateSelected,
  trackExperienceFilterApplied,
  trackExperienceFilterOpened,
  trackLanguageFilterApplied,
  trackLanguageFilterOpened,
  trackLocationFilterApplied,
  trackLocationFilterOpened,
} from './tracking';

type FilterControlsProps = {
  locations: UpcomingEventsPageData['locations'];
  languages: UpcomingEventsPageData['languages'];
  tags: UpcomingEventsPageData['tags'];
  experiences: UpcomingEventsPageData['experiences'];
  /** The `from` the loader resolved — the URL's value, or today. */
  from: string;
};

/**
 * The page's filter controls, presented the way home presents its search:
 * an inline `FilterBar` of segmented triggers on desktop, and on mobile a
 * single "Filters" button opening the shared `FilterSheet`.
 *
 * The date filter is deliberately outside the sheet's `fields` (it is not a
 * multi-select) and stays visible on mobile alongside the Filters button —
 * "from when" is the control visitors reach for most on this page, so burying
 * it behind a dialog would be the wrong trade.
 */
export function FilterControls({
  locations,
  languages,
  tags,
  experiences,
  from,
}: FilterControlsProps) {
  const submit = useSubmit();
  const [searchParams] = useSearchParams();
  const navigation = useNavigation();
  const location = useLocation();
  const intl = useIntl();
  const [sheetOpen, setSheetOpen] = useState(false);
  usePreloadFilterSheet();

  // Prefer the in-flight navigation's params so a trigger updates the moment
  // it's clicked, instead of after the loader returns. React Router clears
  // `navigation.location` on commit or abort, so this rolls back by itself if
  // the navigation is cancelled.
  //
  // Only while the pending navigation stays on this page. An outgoing one (a
  // card click) targets a path with no filter params — reading those would
  // blank every trigger for a frame before unmount.
  //
  // Read into plain locals before the dependency array: optional-chained
  // member expressions as deps defeat the React Compiler's memoization check
  // (`react-hooks/preserve-manual-memoization`).
  const pendingPathname = navigation.location?.pathname;
  const pendingSearch = navigation.location?.search;
  const activeParams = useMemo(() => {
    if (pendingPathname === location.pathname && pendingSearch !== undefined) {
      return new URLSearchParams(pendingSearch);
    }
    return searchParams;
  }, [pendingPathname, pendingSearch, location.pathname, searchParams]);

  const selected = useMemo(
    () => ({
      locations: activeParams.getAll('locations'),
      languages: activeParams.getAll('languages'),
      // Only the tags this page can name: an id with no option would seed the
      // sheet's draft and be written straight back to the URL on submit.
      tags: pick(tags, activeParams.getAll('tags')).map((tag) => tag.id),
      experiences: activeParams.getAll('experiences'),
      // `startDate` is the page's; `from` on this same URL is the dialog's.
      from: activeParams.get('startDate') ?? from,
    }),
    [tags, activeParams, from]
  );

  /**
   * Commit a filter change by SUBMITTING, not by writing search params
   * directly. This page's feed is filtered server-side, so the loader has to
   * re-run — `setSearchParams` would change the URL and leave the list stale.
   *
   * `replace` keeps the back button useful: a filter tweak isn't somewhere the
   * visitor wants to return to, and pushing every toggle would bury the page
   * they arrived from.
   */
  const apply = (updates: FilterUpdates) => {
    void submit(buildFilterSearch(searchParams, updates), {
      method: 'get',
      replace: true,
      preventScrollReset: true,
    });
  };

  // Hide a dimension with nothing useful to choose — one option (or none)
  // gives the visitor no way to narrow, so the trigger is just noise. The date
  // filter always shows: every storefront has dates.
  const showExperiences = experiences.length > 1;
  const showLocations = locations.length > 1;
  const showLanguages = languages.length > 1;
  const showTags = tags.length > 1;

  // `FilterBar` shrinks its max-width when only one trigger is shown, so it
  // needs the count — the date filter included, since it sits in the bar.
  const filterCount =
    1 +
    (showExperiences ? 1 : 0) +
    (showLocations ? 1 : 0) +
    (showLanguages ? 1 : 0) +
    (showTags ? 1 : 0);

  const m = FILTER_SHEET_MESSAGES;
  const sheetFields: FilterSheetField[] = [
    {
      key: 'experiences',
      label: intl.formatMessage(m.experienceLabel),
      options: showExperiences
        ? experiences.map((e) => ({ value: e.id, label: e.name }))
        : [],
      value: selected.experiences,
      emptyLabel: intl.formatMessage(m.experienceEmpty),
      manyLabel: (count) => intl.formatMessage(m.experienceMany, { count }),
    },
    {
      key: 'locations',
      label: intl.formatMessage(m.locationLabel),
      options: showLocations
        ? locations.map((l) => ({ value: l.id, label: l.label }))
        : [],
      value: selected.locations,
      emptyLabel: intl.formatMessage(m.locationEmpty),
      manyLabel: (count) => intl.formatMessage(m.locationMany, { count }),
    },
    {
      key: 'languages',
      label: intl.formatMessage(m.languageLabel),
      options: showLanguages
        ? languages.map((l) => ({ value: l.id, label: l.label }))
        : [],
      value: selected.languages,
      emptyLabel: intl.formatMessage(m.languageEmpty),
      manyLabel: (count) => intl.formatMessage(m.languageMany, { count }),
    },
    {
      key: 'tags',
      label: intl.formatMessage(m.categoryLabel),
      options: showTags
        ? tags.map((t) => ({ value: t.id, label: t.name }))
        : [],
      value: selected.tags,
      emptyLabel: intl.formatMessage(m.categoryEmpty),
      manyLabel: (count) => intl.formatMessage(m.categoryMany, { count }),
    },
  ];

  const hasSheetFields = sheetFields.some((f) => f.options.length > 0);

  const dateFilter = (
    <DateFromFilter
      value={selected.from}
      onOpen={trackDatePickerOpened}
      onChange={(date) => {
        trackDateSelected(date);
        apply({ startDate: date });
      }}
    />
  );

  return (
    <>
      {/* Desktop: the segmented bar, matching the home page's search bar —
          but stretched, because here it heads a grid that already spans the
          full width, and a centred 1000px bar above it reads as misaligned. */}
      <div className={desktopOnlyStyles}>
        <FilterBar filterCount={filterCount} fullWidth>
          {dateFilter}

          {showExperiences && (
            <ExperienceFilter
              value={selected.experiences}
              options={experiences}
              onOpen={trackExperienceFilterOpened}
              onChange={(ids) => {
                trackExperienceFilterApplied(pick(experiences, ids));
                apply({ experiences: ids });
              }}
            />
          )}

          {showLocations && (
            <LocationFilter
              value={selected.locations}
              options={locations}
              onOpen={trackLocationFilterOpened}
              onChange={(ids) => {
                trackLocationFilterApplied(pick(locations, ids));
                apply({ locations: ids });
              }}
            />
          )}

          {showLanguages && (
            <LanguageFilter
              value={selected.languages}
              options={languages}
              onOpen={trackLanguageFilterOpened}
              onChange={(ids) => {
                trackLanguageFilterApplied(pick(languages, ids));
                apply({ languages: ids });
              }}
            />
          )}

          {showTags && (
            <CategoryFilter
              value={selected.tags}
              options={tags}
              onChange={(ids) => apply({ tags: ids })}
            />
          )}
        </FilterBar>
      </div>

      {/* Mobile: the date stays out in the open, everything else behind one
          button — the same shape as home's "Start searching". */}
      <div className={mobileOnlyStyles}>
        {dateFilter}

        {hasSheetFields && (
          <>
            <Button
              appearance="secondary"
              size="large"
              onClick={() => setSheetOpen(true)}
              className={filtersButtonStyles}
            >
              <SearchIcon size="medium" />
              <FormattedMessage
                defaultMessage="Filters"
                description="Mobile-only button on the upcoming events page that opens the filter dialog"
              />
            </Button>

            {sheetOpen && (
              <FilterSheet
                title={
                  <FormattedMessage
                    defaultMessage="Narrow your search"
                    description="Heading of the upcoming events filter dialog (mobile)"
                  />
                }
                submitLabel={
                  <FormattedMessage
                    defaultMessage="Show events"
                    description="Primary action in the upcoming events filter dialog (mobile)"
                  />
                }
                fields={sheetFields}
                onClose={() => setSheetOpen(false)}
                onSubmit={(selection) => {
                  apply({
                    experiences: selection.experiences ?? [],
                    locations: selection.locations ?? [],
                    languages: selection.languages ?? [],
                    tags: selection.tags ?? [],
                  });
                  setSheetOpen(false);
                }}
              />
            )}
          </>
        )}
      </div>
    </>
  );
}

const desktopOnlyStyles = css({
  display: { base: 'none', tablet: 'block' },
});

const mobileOnlyStyles = css({
  display: { base: 'flex', tablet: 'none' },
  flexDirection: 'row',
  gap: 'small',
  alignItems: 'center',
  width: 'full',
});

const filtersButtonStyles = css({
  flex: '1',
});
